import * as React from 'react';
import { Transforms } from 'slate';
import type { BaseRange } from 'slate';
import type { ReactEditor } from 'slate-react';

import { SelectMention } from './select-mention';
import type {
  handleMentionKeyEventType,
  handleMentionSelectionType,
} from './types';
import { useMentionQuery } from './use-mention-query';
import { getTextAfterSymbol, insertMention } from './utils';

export const useMention = () => {
  const editorRef = React.useRef<ReactEditor | null>(null);

  const [target, setTarget] = React.useState<BaseRange | null>(null);
  const [query, setQuery] = React.useState('');
  const [searchUsers, setSearchUsers] = React.useState(true);
  const [index, setIndex] = React.useState(0);

  const { users, loading } = useMentionQuery(query);

  const handleMentionSelection: handleMentionSelectionType = (user) => {
    if (user && editorRef?.current) {
      if (target) Transforms.select(editorRef?.current, target);
      insertMention(editorRef?.current, user);

      // clear user selection to prevent blinking popover with previous query results
      setTarget(null);
      setQuery('');
    }
  };

  const handleMentionKeyEvent: handleMentionKeyEventType = React.useCallback(
    (event, editor) => {
      editorRef.current = editor;

      if (event.key === '@') setSearchUsers(true);

      if (target && users.length) {
        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault();
            const nextIndex = index >= users.length - 1 ? 0 : index + 1;
            setIndex(nextIndex);
            break;
          }
          case 'ArrowUp': {
            event.preventDefault();
            const prevIndex = index <= 0 ? users.length - 1 : index - 1;
            setIndex(prevIndex);
            break;
          }
          case 'Tab':
          case 'Enter': {
            event.preventDefault();
            handleMentionSelection(users[index]);
            break;
          }
          case 'Escape': {
            event.preventDefault();
            setTarget(null);
            setSearchUsers(false);
            break;
          }
        }
      }
    },
    [index, target, users]
  );

  const handleMentionOnChange = () => {
    if (editorRef.current) {
      const { query, range } = getTextAfterSymbol(editorRef.current);
      if (query && range && searchUsers) {
        setTarget(range);
        setQuery(query);
        setIndex(0);
      } else setTarget(null);
    }
  };

  // remove popover for empty result list
  React.useEffect(() => {
    if (!loading && !users.length) setTarget(null);
  }, [users, loading]);

  return {
    handleMentionKeyEvent,
    handleMentionOnChange,
    // @ts-expect-error Leftover after Brewery migration
    SelectMention: (restProps) => (
      <SelectMention
        {...{ ...restProps, target, editor: editorRef.current }}
        {...{
          popoverContentProps: {
            index,
            loading,
            onMouseDown: handleMentionSelection,
            users,
          },
        }}
      />
    ),
  };
};
